// @flow
/** @jsx jsx */

import * as React from 'react';

import Icon from '@zapier/common-components/src/components/Icon';
import Media from '@zapier/common-components/src/components/Media';
import { Colors } from '@zapier/theme';
import { createKeyHandler } from '@zapier/common-utils';
import { css, jsx } from '@emotion/core';

type Props = {
  /** Whether the URL of the video should be displayed in the overlay */
  showUrl?: boolean,
  /** Title that displays below the play button */
  title?: string,
  /** URL to the embeddable video that will load in an iframe */
  url: string,
  /** Overlay image that will show before the user presses the play button */
  overlayUrl?: string,
  /** Optional border radius */
  borderRadius?: number,
};

type State = {
  isOverlayClicked: boolean,
};

const mq = {
  small: '@media screen and (min-width: 800px)',
};

const containerStyle = css({
  borderRadius: '50px',
  position: 'relative',
  paddingBottom: '56.25%', // 16 x 9
});

const overlayStyle = (transparent: boolean) =>
  css({
    position: 'absolute',
    display: 'flex',
    width: '100%',
    height: '100%',
    textAlign: 'center',
    zIndex: 1,
    cursor: 'pointer',
    transition: 'opacity 140ms cubic-bezier(0.65, 0.05, 0.36, 1)',
    backgroundColor: transparent ? 'transparent' : Colors.deepSpace,
    ':hover': {
      opacity: 0.7,
    },
  });

const overlayContentStyle = css({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: '100%',
});

const overlayImageStyle = (url: string, borderRadius?: number) =>
  css({
    position: 'absolute',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    borderRadius,
    backgroundImage: `url(${url})`,
  });

const playArrowContainerStyle = (small: boolean) =>
  css({
    marginBottom: small ? 24 : 12,
  });

const titleStyle = css({
  color: Colors.white,
  fontSize: 16,
  fontWeight: 300,
  lineHeight: '22px',
  '@media screen and (min-width: 800px)': {
    fontSize: 20,
    fontWeight: 600,
    lineHeight: '28px',
  },
});

const urlStyle = css({
  color: Colors.steel,
  fontSize: 16,
  lineHeight: '22px',
  display: 'none',
  padding: 10,
  wordBreak: 'break-all',
  '@media screen and (min-width: 800px)': {
    display: 'block',
  },
});

const iframeStyle = css({
  position: 'absolute',
  width: '100%',
  height: '100%',
  backgroundColor: Colors.deepSpace,
});

class VideoPlayer extends React.Component<Props, State> {
  static defaultProps = {
    borderRadius: 0,
  };

  state = {
    isOverlayClicked: false,
  };

  onClickOverlay = () => {
    this.setState({
      isOverlayClicked: true,
    });
  };

  render() {
    const { props } = this;
    const { overlayUrl, borderRadius } = props;
    const { isOverlayClicked } = this.state;

    return (
      <div css={containerStyle}>
        {!isOverlayClicked && (
          <div
            aria-label="Play video"
            css={overlayStyle(Boolean(overlayUrl))}
            onClick={this.onClickOverlay}
            onKeyDown={createKeyHandler(['enter'], this.onClickOverlay)}
            role="button"
            tabIndex={0}
          >
            <div css={overlayContentStyle}>
              <Media queries={mq}>
                {({ small }) => (
                  <div css={playArrowContainerStyle(small)}>
                    <Icon
                      color="deepSpace"
                      name="zap-arrow"
                      size={small ? 40 : 28}
                    />
                  </div>
                )}
              </Media>
              {props.title && <span css={titleStyle}>{props.title}</span>}
              {props.showUrl && <span css={urlStyle}>{props.url}</span>}
            </div>
            {overlayUrl && (
              <div css={overlayImageStyle(overlayUrl, borderRadius)} />
            )}
          </div>
        )}
        {isOverlayClicked && (
          <iframe
            allow="autoplay; encrypted-media"
            allowFullScreen={true}
            css={iframeStyle}
            frameBorder="0"
            src={props.url}
            title={props.title || 'video'}
          />
        )}
      </div>
    );
  }
}

export default VideoPlayer;
